// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The satellites' engine: one per page, shared by every renderer (the
 * browser globes and the headset/phone scene). It owns the object list,
 * which objects show and in what colour, and the positions the renderers
 * read each frame:
 *
 * - `current`: Earth-fixed km at the frame's time, interpolated between two
 *   SGP4 snapshots the worker computes ahead;
 * - the trail ring: TRAIL_SLOTS samples, one every TRAIL_STEP_S of sim
 *   time, slot = sample number mod TRAIL_SLOTS. A renderer draws the
 *   segment that ends at a slot only when `slotHasPrevious(slot)`.
 *
 * Counters (`layoutVersion`, `styleVersion`, `trailVersion`) tell a
 * renderer what to re-upload; `takeDirtySlots` says which trail slots
 * changed since that renderer last asked.
 */
import type { WorkerCall, WorkerReply, WorkerRequest } from './satellitePropagatorProtocol'
import type { SatelliteInfo, SatelliteOmm } from './satelliteTypes'
import { orbitClass, orbitShape } from './satelliteOrbit'

export const TRAIL_SLOTS = 12
export const TRAIL_STEP_S = 30
const TRAIL_STEP_MS = TRAIL_STEP_S * 1000

/** Names that get a station's ring and label. */
const STATION_NAME = /^(ISS \(ZARYA\)|CSS \(TIANHE\))/

export interface SatellitePropagatorPort {
  postMessage(msg: WorkerRequest, transfer?: Transferable[]): void
  onmessage: ((ev: MessageEvent<WorkerReply>) => void) | null
  terminate(): void
}

interface Snapshot {
  t: number
  data: Float32Array
}

export function infoFromOmm(omm: SatelliteOmm, source: string): SatelliteInfo {
  const shape = orbitShape(omm)
  const name = omm.OBJECT_NAME ?? `NORAD ${omm.NORAD_CAT_ID}`
  return {
    norad: omm.NORAD_CAT_ID,
    name,
    cosparId: omm.OBJECT_ID,
    epoch: omm.EPOCH,
    orbit: orbitClass(shape),
    periodMin: shape.periodMin,
    semiMajorKm: shape.semiMajorKm,
    perigeeKm: shape.perigeeKm,
    apogeeKm: shape.apogeeKm,
    inclinationDeg: shape.inclinationDeg,
    eccentricity: shape.eccentricity,
    source,
    country: 'other',
    station: STATION_NAME.test(name),
    starlink: name.startsWith('STARLINK'),
  }
}

export class SatelliteEngine {
  infos: SatelliteInfo[] = []
  /** 1 = drawn. */
  mask: Uint8Array = new Uint8Array(0)
  /** sRGB 0..1, three per object. */
  colour: Float32Array = new Float32Array(0)
  /** Earth-fixed km at the last `update` time; NaN when unknown. */
  current: Float32Array = new Float32Array(0)
  /** TRAIL_SLOTS × n × 3, Earth-fixed km. */
  trail: Float32Array = new Float32Array(0)
  /** Sample number held by each slot (NaN = empty). */
  readonly slotSample = new Float64Array(TRAIL_SLOTS).fill(NaN)
  layoutVersion = 0
  styleVersion = 0
  trailVersion = 0
  /** Sim time of `current`. */
  time = 0

  private worker: SatellitePropagatorPort
  private reqId = 0
  private pending = new Map<number, { resolve: (r: WorkerReply) => void; reject: (e: Error) => void }>()
  private a: Snapshot | null = null
  private b: Snapshot | null = null
  private snapPending = false
  private trailPending = false
  /** Bumped on every reset so late replies are dropped. */
  private epoch = 0
  private listeners = new Set<() => void>()
  /** Per reader: trail slots written since it last asked. */
  private dirty = new Map<object, Set<number>>()

  constructor(worker: SatellitePropagatorPort) {
    this.worker = worker
    worker.onmessage = (ev) => {
      const msg = ev.data
      const p = this.pending.get(msg.reqId)
      if (!p) return
      this.pending.delete(msg.reqId)
      if (msg.type === 'error') p.reject(new Error(msg.message))
      else p.resolve(msg)
    }
  }

  get count(): number {
    return this.infos.length
  }

  private call(msg: WorkerCall, transfer: Transferable[] = []): Promise<WorkerReply> {
    const reqId = ++this.reqId
    return new Promise((resolve, reject) => {
      this.pending.set(reqId, { resolve, reject })
      this.worker.postMessage({ ...msg, reqId } as WorkerRequest, transfer)
    })
  }

  /** Loads one GP group file; resolves with the new objects' first index. */
  async loadGroup(source: string, buffer: ArrayBuffer, sha256?: string): Promise<number> {
    const skipNorad = this.infos.map((i) => i.norad)
    const reply = await this.call(
      { type: 'load', source, buffer, sha256, skipNorad: skipNorad.length ? skipNorad : undefined },
      [buffer],
    )
    if (reply.type !== 'loaded') throw new Error('unexpected reply')
    const added = reply.omms.map((o) => infoFromOmm(o, source))
    const n = this.infos.length + added.length
    this.infos = this.infos.concat(added)
    this.mask = grow(this.mask, n)
    this.colour = growF(this.colour, n * 3)
    this.current = new Float32Array(n * 3).fill(NaN)
    this.trail = new Float32Array(TRAIL_SLOTS * n * 3).fill(NaN)
    this.slotSample.fill(NaN)
    this.layoutVersion++
    this.reset()
    this.emit()
    return reply.start
  }

  /** Forgets every object, before a new GP release is loaded. */
  async clear(): Promise<void> {
    await this.call({ type: 'clear' })
    this.infos = []
    this.mask = new Uint8Array(0)
    this.colour = new Float32Array(0)
    this.current = new Float32Array(0)
    this.trail = new Float32Array(0)
    this.slotSample.fill(NaN)
    this.markAllDirty()
    this.layoutVersion++
    this.reset()
    this.emit()
  }

  /** Sets which objects show; `colours` is sRGB 0..1, three per object. */
  setStyle(mask: Uint8Array, colours: Float32Array): void {
    const changedMask = !sameBytes(mask, this.mask)
    this.mask = mask
    this.colour = colours
    this.styleVersion++
    if (changedMask) {
      void this.call({ type: 'mask', mask: mask.slice() }).catch(() => {})
      // Hidden objects were never propagated: start the trails again.
      this.slotSample.fill(NaN)
      this.trail.fill(NaN)
      this.markAllDirty()
      this.trailVersion++
      this.reset()
    }
    this.emit()
  }

  /** Drops the snapshots so the next `update` asks for fresh ones. */
  reset(): void {
    this.epoch++
    this.a = this.b = null
    this.snapPending = false
    this.trailPending = false
  }

  /**
   * Moves `current` to sim time `t`. `speed` (sim s per real s) sets how
   * far ahead the next snapshot is computed.
   */
  update(t: number, speed: number): void {
    this.time = t
    if (!this.count) return
    const step = Math.min(60_000, Math.max(1000, Math.abs(speed) * 1000))
    const dir = speed < 0 ? -1 : 1
    // Far from what we hold (a seek): start over at t.
    if (this.a && this.b) {
      const lo = Math.min(this.a.t, this.b.t) - 2 * step
      const hi = Math.max(this.a.t, this.b.t) + 2 * step
      if (t < lo || t > hi) this.reset()
    }
    if (!this.snapPending) {
      if (!this.a) this.requestSnapshots([t, t + dir * step], false)
      else if (!this.b) this.requestSnapshots([this.a.t + dir * step], true)
      else if (dir > 0 ? t >= this.b.t : t <= this.b.t) this.requestSnapshots([this.b.t + dir * step], true)
    }
    this.interpolate(t)
    this.updateTrail(t)
  }

  private requestSnapshots(times: number[], append: boolean): void {
    this.snapPending = true
    const epoch = this.epoch
    void this.call({ type: 'positions', timesMs: times })
      .then((r) => {
        if (epoch !== this.epoch || r.type !== 'positions' || r.count !== this.count) return
        const n3 = r.count * 3
        const snaps = times.map((t, k) => ({ t, data: r.data.subarray(k * n3, (k + 1) * n3) }))
        if (append && this.a && this.b) {
          this.a = this.b
          this.b = snaps[0]
        } else if (append && this.a) {
          this.b = snaps[0]
        } else {
          this.a = snaps[0]
          this.b = snaps[1] ?? null
        }
        this.emit()
      })
      .catch(() => {})
      .finally(() => {
        if (epoch === this.epoch) this.snapPending = false
      })
  }

  private interpolate(t: number): void {
    const a = this.a
    const b = this.b
    const out = this.current
    if (!a) {
      out.fill(NaN)
      return
    }
    if (!b || b.t === a.t) {
      out.set(a.data)
      return
    }
    const s = Math.min(1.5, Math.max(-0.5, (t - a.t) / (b.t - a.t)))
    const ad = a.data
    const bd = b.data
    for (let i = 0; i < out.length; i += 3) {
      const ax = ad[i]
      const bx = bd[i]
      if (ax !== ax || bx !== bx) {
        out[i] = out[i + 1] = out[i + 2] = NaN
        continue
      }
      // Lerp, then put the point back at the interpolated radius so a
      // long step doesn't cut the corner inside the orbit.
      const x = ax + (bx - ax) * s
      const y = ad[i + 1] + (bd[i + 1] - ad[i + 1]) * s
      const z = ad[i + 2] + (bd[i + 2] - ad[i + 2]) * s
      const ra = Math.hypot(ax, ad[i + 1], ad[i + 2])
      const rb = Math.hypot(bx, bd[i + 1], bd[i + 2])
      const k = (ra + (rb - ra) * s) / (Math.hypot(x, y, z) || 1)
      out[i] = x * k
      out[i + 1] = y * k
      out[i + 2] = z * k
    }
  }

  /** Keeps the ring on the samples it should hold at `t`: the newest is the last at or before t. */
  private updateTrail(t: number): void {
    if (this.trailPending) return
    const newest = Math.floor(t / TRAIL_STEP_MS)
    const missing: number[] = []
    for (let k = newest - TRAIL_SLOTS + 1; k <= newest; k++) {
      if (this.slotSample[mod(k, TRAIL_SLOTS)] !== k) missing.push(k)
    }
    if (!missing.length) return
    this.trailPending = true
    const epoch = this.epoch
    void this.call({ type: 'positions', timesMs: missing.map((k) => k * TRAIL_STEP_MS) })
      .then((r) => {
        if (epoch !== this.epoch || r.type !== 'positions' || r.count !== this.count) return
        const n3 = r.count * 3
        const touched: number[] = []
        missing.forEach((k, j) => {
          const slot = mod(k, TRAIL_SLOTS)
          this.trail.set(r.data.subarray(j * n3, (j + 1) * n3), slot * n3)
          this.slotSample[slot] = k
          // The next slot's segment starts here: it may have just become drawable.
          touched.push(slot, mod(k + 1, TRAIL_SLOTS))
        })
        for (const set of this.dirty.values()) for (const s of touched) set.add(s)
        this.trailVersion++
        this.emit()
      })
      .catch(() => {})
      .finally(() => {
        if (epoch === this.epoch) this.trailPending = false
      })
  }

  /** The trail slots written since `reader` last asked (all of them the first time). */
  takeDirtySlots(reader: object): number[] {
    let set = this.dirty.get(reader)
    if (!set) {
      set = new Set(Array.from({ length: TRAIL_SLOTS }, (_, i) => i))
      this.dirty.set(reader, set)
    }
    const out = [...set]
    set.clear()
    return out
  }

  /** Stops tracking a reader's dirty slots. */
  releaseReader(reader: object): void {
    this.dirty.delete(reader)
  }

  private markAllDirty(): void {
    for (const set of this.dirty.values()) for (let s = 0; s < TRAIL_SLOTS; s++) set.add(s)
  }

  /** Sim time (ms) of the sample a slot holds, NaN when empty. */
  slotTime(slot: number): number {
    return this.slotSample[slot] * TRAIL_STEP_MS
  }

  /** True when the segment ending at `slot` has its start in the ring. */
  slotHasPrevious(slot: number): boolean {
    const k = this.slotSample[slot]
    return k === k && this.slotSample[mod(slot - 1, TRAIL_SLOTS)] === k - 1
  }

  /** The slot holding the newest sample, or -1. */
  newestSlot(): number {
    let best = -1
    let bestK = -Infinity
    for (let s = 0; s < TRAIL_SLOTS; s++) {
      const k = this.slotSample[s]
      if (k === k && k > bestK) {
        bestK = k
        best = s
      }
    }
    return best
  }

  /** One object's path from startMs to endMs, Earth-fixed km. */
  async orbitPath(
    index: number,
    startMs: number,
    endMs: number,
    samples: number,
    inertialAtMs?: number,
  ): Promise<Float32Array> {
    const r = await this.call({ type: 'orbit', index, startMs, endMs, samples, inertialAtMs })
    if (r.type !== 'orbit') throw new Error('unexpected reply')
    return r.data
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private emit(): void {
    for (const fn of this.listeners) fn()
  }

  dispose(): void {
    this.worker.terminate()
    this.listeners.clear()
    this.pending.clear()
    this.dirty.clear()
  }
}

export function mod(a: number, n: number): number {
  return ((a % n) + n) % n
}

function grow(a: Uint8Array, n: number): Uint8Array {
  const out = new Uint8Array(n)
  out.set(a.subarray(0, Math.min(a.length, n)))
  return out
}

function growF(a: Float32Array, n: number): Float32Array {
  const out = new Float32Array(n)
  out.set(a.subarray(0, Math.min(a.length, n)))
  return out
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/** Earth-fixed km → geocentric longitude, latitude (degrees) and height over the 6371 km sphere (km). */
export function ecfToLonLat(x: number, y: number, z: number): [number, number, number] {
  const r = Math.hypot(x, y, z)
  return [(Math.atan2(y, x) * 180) / Math.PI, (Math.asin(z / r) * 180) / Math.PI, r - 6371]
}
